import React, { useState, useEffect, useRef } from "react";
import "./VendorAuditDashboard.css"
import VendorAuditDashboardFilter from "./VendorAuditDashboardFilter";
import Chart from "./Chart";
import VendorAuditBarChart from "./VendorAuditBarChart";
import { useGetOverallScores, useGetComplianceScoreTrends } from "../../../backend/masters";

const VendorAuditDashboard = () => {
  const currentYear = new Date().getFullYear();

  const [locationFilters, setLocationFilter] = useState<any>({
    year: currentYear,
  });
  const [previousFilters, setPreviousFilters] = useState<any>();
  const [chartCategory, setChartCategory] = useState("month");
  const [currentPage, setCurrentPage] = useState(1);

  const previousFiltersRef = useRef();
  previousFiltersRef.current = previousFilters;

  const getAPIPayload = (filters: any, trendType?: string, page: number = 1) => {
   if (!filters) return null;

   // Helper to convert month number (1-12) to month name string
   const getMonthName = (monthNum: number) => {
     const monthNames = [
       'January', 'February', 'March', 'April', 'May', 'June',
       'July', 'August', 'September', 'October', 'November', 'December'
     ];
     return monthNames[monthNum - 1];
   };

   // Get current year
   const currentYear = new Date().getFullYear();

   // Mandatory: year and trend_type
   // Company_id: use all_company_ids if no specific company selected
   const payload: any = {
     company_id: filters.company ? [filters.company] : (filters.all_company_ids || []),
    //  year: filters.year ? String(filters.year) : String(currentYear),
     page: page,
     per_page: 6,
     trend_type: trendType || 'month'
   };

    // Add optional filters only if they exist
    if (filters.year) {
      payload.year = String(filters.year);
    }
    if (filters.associate_company) {
      payload.associate_company_id = [filters.associate_company];
    }
    if (filters.location_id) {
      payload.location_id = [filters.location_id];
    }
    if (filters.vendor_category_id) {
      payload.vendor_category_id = [filters.vendor_category_id];
    }
    if (filters.vendor_registration_id) {
      payload.vendor_registration_id = [filters.vendor_registration_id];
    }
    if (filters.month) {
      payload.month = getMonthName(filters.month);
    }

   return payload;
 };

 const apiPayload = getAPIPayload(locationFilters);
 const complianceTrendsPayload = getAPIPayload(locationFilters, chartCategory, currentPage);
 const {
   overallCriticalScore,
   overallScore,
   isFetching: fetchingScores,
   refetch: refetchScores
 } = useGetOverallScores(
   apiPayload,
  //  Boolean(locationFilters && locationFilters.year && (locationFilters.company || locationFilters.all_company_ids))
  Boolean(locationFilters && (locationFilters.company || locationFilters.all_company_ids))
 );

 const {
   complianceScoreTrends,
   isFetching: fetchingTrends,
   refetch: refetchTrends
 } = useGetComplianceScoreTrends(
    complianceTrendsPayload,
  //  Boolean(locationFilters && locationFilters.year && (locationFilters.company || locationFilters.all_company_ids))
    Boolean(locationFilters && (locationFilters.company || locationFilters.all_company_ids))
 );

  function onLocationChange(event: any, filter: any = {}) {
    // Preserve year if not provided in event
    const updatedFilters = {
      year: currentYear,
      ...event
    };
    setLocationFilter(updatedFilters);
    setCurrentPage(1); // Reset to page 1 when filters change
    if(Object.keys(filter).length > 0){
      const _filter = {
        ...previousFilters,
        ...filter
      }
      setPreviousFilters(_filter);
    }
  }

  useEffect(() => {
    if (locationFilters && (locationFilters.company || locationFilters.all_company_ids)) {
      refetchScores();
    }
  }, [
   JSON.stringify(locationFilters),
  ]);
  useEffect(() => {
    if (locationFilters && (locationFilters.company || locationFilters.all_company_ids)) {
      refetchTrends();
    }
  }, [
    JSON.stringify(locationFilters),
    chartCategory,
    currentPage
  ]);

  const handleNextPage = () => {
    if (complianceScoreTrends && complianceScoreTrends.total_count) {
      const totalPages = Math.ceil(complianceScoreTrends.total_count / 6);
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const totalPages = complianceScoreTrends && complianceScoreTrends.total_count
    ? Math.ceil(complianceScoreTrends.total_count / 6)
    : 1;
  const isPreviousDisabled = currentPage === 1 || fetchingTrends;
  const isNextDisabled = currentPage >= totalPages || fetchingTrends;
  const showPaginationArrows = complianceScoreTrends && complianceScoreTrends.total_count > 6;

  return (
    <section className="VendorAuditDashboardWrapper">
      <div className="d-flex flex-column">
        <div className="d-flex  p-2 align-items-center pageHeading shadow">
          <h4 className="mb-0 fw-bold">Vendor Audit Dashboard</h4>
        </div>

        <form className="p-0 mx-3 my-2">
          <div className="card shadow border-0 mt-2 mb-3">
            <div className="d-flex align-items-center filters">
              <VendorAuditDashboardFilter onChange={onLocationChange} previousFilters={previousFilters} />
            </div>
          </div>
        </form>
      </div>

      <section className="VendorAuditDashboardContainer">
        <div className="d-flex donut-graph gap-5">
          <div className="card p-3 w-50">
            <p style={{ fontSize: "16px", fontWeight: "bold" }}>Overall Compliance Score %</p>
            <div><Chart data={overallScore} keys={['compliant', 'non_compliant']} scoreColor="#58AAFC" isLoading={fetchingScores}/></div>
          </div>
          <div className="card py-3 px-4 w-50">
            <p style={{ fontSize: "16px", fontWeight: "bold" }}>Overall Critical Score %</p>
            <div><Chart data={overallCriticalScore} keys={['compliant', 'non_compliant']} scoreColor="#477DBA" isLoading={fetchingScores} /></div>
          </div>
        </div>
        
        <div className="bar-chart-section">
          {/* Custom Tabs - positioned above card */}
          <div className="d-flex justify-content-end">
            <div className="chart-tabs">
              <button
                className={`chart-tab ${chartCategory === "month" ? "active" : ""}`}
                 onClick={() => {
                    setChartCategory("month");
                    setCurrentPage(1);
                  }}
              >
                Month-wise
              </button>
              <button
                className={`chart-tab ${chartCategory === "city" ? "active" : ""}`}
                onClick={() => {
                    setChartCategory("city");
                    setCurrentPage(1);
                  }}
              >
                City-wise
              </button>
              <button
                className={`chart-tab ${chartCategory === "vendor" ? "active" : ""}`}
                onClick={() => {
                    setChartCategory("vendor");
                    setCurrentPage(1);
                  }}
              >
                Vendor-wise
              </button>
            </div>
        </div>

        {/* Card with chart - added class 'bar-chart-card-wrapper' for relative positioning */}
        <div className="card bar-chart-card-wrapper"> 
          
          {/* Previous/Left Arrow Button */}
          {showPaginationArrows && (
            <button
              className="chart-arrow left-arrow"
              onClick={handlePreviousPage}
              disabled={isPreviousDisabled}
            >
              &lt;
            </button>
          )}

          {/* Bar Chart Component */}
          <VendorAuditBarChart data={complianceScoreTrends} category={chartCategory} loading={fetchingTrends} />

          {/* Next/Right Arrow Button */}
          {showPaginationArrows && (
            <button
              className="chart-arrow right-arrow"
              onClick={handleNextPage}
              disabled={isNextDisabled}
            >
              &gt;
            </button>
          )}
        </div>
      </div>
      </section>
    </section>
  )
}

export default VendorAuditDashboard
